<template>
  <!-- Search Modal -->
  <Teleport to="body">
    <Transition name="modal">
      <div 
        v-if="isOpen"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click="closeModal"
      >
        <!-- Modal Backdrop -->
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"></div>
        
        <!-- Modal Container -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div 
            class="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl"
            @click.stop
          >
            <!-- Header -->
            <div class="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 class="text-2xl font-bold text-gray-900">全局搜索</h2>
              <button 
                @click="closeModal"
                class="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
              >
                <XMarkIcon class="w-6 h-6" />
              </button>
            </div>

            <!-- Search Input -->
            <div class="p-6">
              <div class="relative">
                <MagnifyingGlassIcon class="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索文章、项目、新闻..."
                  class="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                  @input="performSearch"
                  @keydown.escape="closeModal"
                  ref="searchInput"
                >
              </div>

              <!-- Search Filters -->
              <div class="flex flex-wrap gap-2 mt-4">
                <button
                  v-for="filter in searchFilters"
                  :key="filter.key"
                  @click="toggleFilter(filter.key)"
                  :class="[
                    'px-4 py-2 rounded-full text-sm font-medium transition-colors',
                    activeFilters.includes(filter.key)
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  ]"
                >
                  {{ filter.label }}
                </button>
              </div>
            </div>

            <!-- Search Results -->
            <div class="max-h-96 overflow-y-auto border-t border-gray-200">
              <!-- Loading State -->
              <div v-if="isSearching" class="p-8 text-center">
                <div class="inline-flex items-center space-x-2">
                  <div class="w-2 h-2 bg-primary-600 rounded-full animate-bounce"></div>
                  <div class="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
                  <div class="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
                </div>
                <p class="text-gray-500 mt-4">正在搜索...</p>
              </div>

              <!-- Results -->
              <div v-else-if="searchResults.length > 0" class="divide-y divide-gray-200">
                <div
                  v-for="result in searchResults"
                  :key="result.id"
                  class="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  @click="navigateToResult(result)"
                >
                  <div class="flex items-start space-x-3">
                    <!-- Icon -->
                    <div class="flex-shrink-0">
                      <component 
                        :is="getTypeIcon(result.type)" 
                        class="w-5 h-5 text-gray-400"
                      />
                    </div>
                    
                    <!-- Content -->
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center space-x-2 mb-1">
                        <h3 class="font-medium text-gray-900 truncate">{{ result.title }}</h3>
                        <span 
                          class="px-2 py-1 text-xs font-medium rounded-full"
                          :class="getTypeColorClass(result.type)"
                        >
                          {{ getTypeLabel(result.type) }}
                        </span>
                      </div>
                      <p class="text-sm text-gray-600 line-clamp-2">{{ result.excerpt }}</p>
                      <p class="text-xs text-gray-400 mt-1">{{ formatDate(result.date) }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Results -->
              <div v-else-if="searchQuery && !isSearching" class="p-8 text-center">
                <div class="text-gray-400 mb-4">
                  <MagnifyingGlassIcon class="w-12 h-12 mx-auto" />
                </div>
                <p class="text-gray-900 font-medium mb-2">未找到相关内容</p>
                <p class="text-gray-500 text-sm">尝试使用其他关键词或调整筛选条件</p>
              </div>

              <!-- Initial State -->
              <div v-else class="p-8 text-center">
                <div class="text-gray-400 mb-4">
                  <MagnifyingGlassIcon class="w-12 h-12 mx-auto" />
                </div>
                <p class="text-gray-500">输入关键词开始搜索</p>
              </div>
            </div>

            <!-- Footer -->
            <div class="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <div class="flex items-center justify-between text-sm text-gray-600">
                <div v-if="searchQuery && !isSearching">
                  找到 {{ searchResults.length }} 个结果
                </div>
                <div v-else class="text-gray-400">
                  按 <kbd class="px-2 py-1 bg-gray-200 rounded text-xs">Esc</kbd> 关闭
                </div>
                <div>
                  <kbd class="px-2 py-1 bg-gray-200 rounded text-xs mr-1">↑</kbd>
                  <kbd class="px-2 py-1 bg-gray-200 rounded text-xs">↓</kbd>
                  导航
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { 
  MagnifyingGlassIcon, 
  XMarkIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  NewspaperIcon
} from '@heroicons/vue/24/outline'

interface SearchResult {
  id: string
  title: string
  excerpt: string
  type: 'article' | 'project' | 'news' | 'page'
  url: string
  date: string
}

const router = useRouter()

const isOpen = ref(false)
const searchQuery = ref('')
const searchResults = ref<SearchResult[]>([])
const isSearching = ref(false)
const activeFilters = ref<string[]>(['all'])
const searchInput = ref<HTMLInputElement>()

const searchFilters = [
  { key: 'all', label: '全部' },
  { key: 'articles', label: '文章' },
  { key: 'projects', label: '项目' },
  { key: 'news', label: '新闻' }
]

// Modal control
const openModal = () => {
  isOpen.value = true
  nextTick(() => {
    searchInput.value?.focus()
  })
}

const closeModal = () => {
  isOpen.value = false
  searchQuery.value = ''
  searchResults.value = []
}

// Search functionality
const performSearch = async () => {
  if (!searchQuery.value.trim()) {
    searchResults.value = []
    return
  }

  isSearching.value = true
  
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // Mock search results
    const mockResults: SearchResult[] = [
      {
        id: '1',
        title: 'AP 集群模块制造项目首批模块顺利发运',
        excerpt: '1月2日，华艾工厂迎来2026年的"开门红"，实现了AP在华艾首个集群模块制造项目的首批模块发运。',
        type: 'news',
        url: '/news/1',
        date: '2026-01-05'
      },
      {
        id: '2',
        title: '空分工程施工总承包',
        excerpt: '专业的空分装置施工总承包服务，涵盖土建、安装、调试全流程，为客户提供一站式解决方案。',
        type: 'article',
        url: '/services/engineering',
        date: '2024-01-01'
      },
      {
        id: '3',
        title: '液化空气美国Triton项目',
        excerpt: '由公司华艾工厂承接制造的法国液化空气集团Triton ID1项目系列模块全部交付港口发运到美国。',
        type: 'project',
        url: '/projects/1',
        date: '2024-12-30'
      }
    ]
    
    searchResults.value = mockResults.filter(result => 
      result.title.includes(searchQuery.value) || 
      result.excerpt.includes(searchQuery.value)
    )
  } catch (error) {
    console.error('Search error:', error)
  } finally {
    isSearching.value = false
  }
}

const toggleFilter = (filterKey: string) => {
  if (filterKey === 'all') {
    activeFilters.value = ['all']
  } else {
    activeFilters.value = activeFilters.value.includes(filterKey)
      ? activeFilters.value.filter(f => f !== filterKey)
      : [...activeFilters.value.filter(f => f !== 'all'), filterKey]
  }
  performSearch()
}

const navigateToResult = (result: SearchResult) => {
  router.push(result.url)
  closeModal()
}

// Helper functions
const getTypeIcon = (type: string) => {
  const icons = {
    article: DocumentTextIcon,
    project: BuildingOfficeIcon,
    news: NewspaperIcon,
    page: DocumentTextIcon
  }
  return icons[type as keyof typeof icons] || DocumentTextIcon
}

const getTypeLabel = (type: string) => {
  const labels = {
    article: '文章',
    project: '项目',
    news: '新闻',
    page: '页面'
  }
  return labels[type as keyof typeof labels] || '其他'
}

const getTypeColorClass = (type: string) => {
  const classes = {
    article: 'bg-blue-100 text-blue-800',
    project: 'bg-green-100 text-green-800',
    news: 'bg-yellow-100 text-yellow-800',
    page: 'bg-gray-100 text-gray-800'
  }
  return classes[type as keyof typeof classes] || 'bg-gray-100 text-gray-800'
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('zh-CN')
}

// Keyboard shortcuts
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
    event.preventDefault()
    openModal()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  // Listen for custom event to open search
  document.addEventListener('open-search', openModal)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('open-search', openModal)
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.9);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.animate-bounce {
  animation: bounce 1.4s ease-in-out infinite both;
}
</style>